<template>
    <div class="bid-component">
        <div class="bid-component__header">
            <div class="bid-component__header-label">Текущая ставка</div>
            <div
                v-if="currentBid"
                class="bid-component__header-value"
            >
                {{ numberFormat(currentBid) }} ₽
            </div>
            <div v-else class="bid-component__header-value">-</div>
        </div>

        <div class="bid-component__body">
            <div class="bid-component__info">
                <div class="bid-component__block">
                    <div class="bid-component__block-label">Стартовая цена</div>
                    <div class="bid-component__block-value">{{ numberFormat(startPrice) }} ₽</div>
                </div>

                <div class="bid-component__block">
                    <div class="bid-component__block-label">Ваша ставка</div>
                    <div
                        v-if="userBid"
                        class="bid-component__block-value bid-component__block-value_active"
                    >
                        {{ numberFormat(userBid) }} ₽
                    </div>
                    <div v-else class="bid-component__block-value bid-component__block-value_active">-</div>
                </div>
            </div>

            <div class="bid-component__controls">
                <div class="bid-component__increment-buttons">
                    <UISimpleButton
                        button-type="outlined"
                        @clicked="increaseBid(10000)"
                    >
                        +10 000 ₽
                    </UISimpleButton>

                    <UISimpleButton
                        button-type="outlined"
                        @clicked="increaseBid(25000)"
                    >
                        +25 000 ₽
                    </UISimpleButton>
                </div>

                <div class="bid-component__manual-input">
                    <UISimpleButton
                        class="bid-input-button"
                        @clicked="decreaseBid"
                    >
                        −
                    </UISimpleButton>
                    <input
                        v-model="formBid"
                        class="bid-input"
                        @input="formatInput"
                    />
                    <UISimpleButton
                        class="bid-input-button"
                        @clicked="increaseBid(AuctionBidMinStep)"
                    >
                        +
                    </UISimpleButton>
                </div>
            </div>

            <UISimpleButton @clicked="placeBid">
                Сделать ставку
            </UISimpleButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, defineProps, defineEmits } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalKey } from '@/injection/keys.ts';

    import { AuctionBidMaxStep, AuctionBidMinStep } from '@/types/auction.type.ts';

    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import AuctionConfirmCreateBid from '@/components/auction/AuctionConfirmCreateBid/AuctionConfirmCreateBid.vue';

    const { auctionId, startPrice, currentBid, userBid } = defineProps({
        auctionId: {
            type: Number,
            required: true,
        },
        startPrice: {
            type: Number,
            required: true,
        },
        currentBid: Number,
        userBid: Number,
    });

    const emit = defineEmits<{
        (e: 'bid'): void,
    }>();

    const $modal = injectStrict(ModalKey);

    const inputBid = ref<number | undefined>((currentBid ?? startPrice) + AuctionBidMinStep);

    const formBid = computed<string>({
        get: () => inputBid.value !== undefined ? numberFormat(inputBid.value) : '',
        set: (formattedValue: string) => {
            const value = formattedValue.replace(/\D/g, '');
            inputBid.value = value === '' ? undefined : parseInt(value);
            fixBidValue();
        },
    });

    function increaseBid(amount: number): void {
        const currentValue = inputBid.value ?? 0;
        inputBid.value = currentValue + amount;
        fixBidValue();
    }

    function decreaseBid(): void {
        const currentValue = inputBid.value ?? 0;
        inputBid.value = currentValue - AuctionBidMinStep;
        fixBidValue();
    }

    function formatInput(event: Event): void {
        if (inputBid.value && inputBid.value >= getMaxBidValue()) {
            const el = event.target as HTMLInputElement;
            el.value = formBid.value;
        }
    }

    function placeBid(): void {
        $modal?.open({
            component: AuctionConfirmCreateBid,
            props: {
                auctionId,
                bidValue: inputBid.value,
                onConfirmed: () => {
                    $modal?.close();
                    emit('bid');
                },
            },
            modalType: 'narrow',
        });
    }

    function fixBidValue(): void {
        if (inputBid.value === undefined || inputBid.value <= 0) {
            inputBid.value = undefined;
        }
        else if (inputBid.value > getMaxBidValue()) {
            inputBid.value = getMaxBidValue();
        }
    }

    function getMaxBidValue(): number {
        return (currentBid ?? startPrice) + AuctionBidMaxStep;
    }
</script>

<style scoped lang="scss">
    @use 'ViewAuctionMakeBid' as *;
</style>
